import { CsChartType, ChartData } from '@cs/components/chart-svg';

/** A parsed, validated chart spec the assistant emitted in a ```chart fenced block. */
export interface CsChartSpec {
	type:   CsChartType;
	title?: string;
	data:   ChartData;   // { labels, datasets: [{ label, data }] }
}

/** One box in a process diagram. `kind` drives the shape; `metric`/`tooltip` are optional annotations. */
export interface CsProcessNode {
	id:       string;
	label:    string;
	kind:     'start' | 'process' | 'decision' | 'end';
	metric?:  string;   // small value chip shown under the label (e.g. "1,240 t")
	tooltip?: string;   // richer text shown on hover; falls back to label + metric
}

/** A directed connector between two nodes; `label` annotates a branch (e.g. "yes"/"no"). */
export interface CsProcessEdge {
	from:   string;
	to:     string;
	label?: string;
}

/** A parsed, validated process/flow diagram the assistant emitted in a ```diagram fenced block. */
export interface CsProcessSpec {
	title?:    string;
	direction: 'LR' | 'TB';   // left→right or top→bottom
	nodes:     CsProcessNode[];
	edges:     CsProcessEdge[];
}

/** One annotation box over a documentation screenshot. Positions are percentages of the image (0–100). */
export interface CsImageHighlight {
	x: number; y: number; width: number; height: number;
	color?:         string;
	thickness?:     number;
	dashed?:        boolean;
	radius?:        number;
	label?:         string;
	labelPosition?: 'top' | 'bottom' | 'left' | 'right' | 'inside';
	tooltip?:       string;
	labelBgOpacity?: number;
	solidOpacity?:   number;
	infoType?:      'info' | 'success' | 'warning' | 'danger';
}

/** A documentation screenshot + read-only annotation overlays (from a ```annotated-image block). */
export interface CsAnnotatedImageSpec {
	src:        string;   // same-origin/relative (e.g. "/api/docs/img/…") or data: — never an external URL
	alt?:       string;
	highlights: CsImageHighlight[];
}

/**
 * One renderable piece of an assistant message. Non-discriminated (optional fields) on purpose:
 * Angular template type-checking narrows `@if (seg.kind === ...)` reliably this way.
 */
export interface CsChatSegment {
	kind: 'markdown' | 'chart' | 'chart-pending' | 'diagram' | 'diagram-pending' | 'tool' | 'stats' | 'artifact' | 'todos' | 'terminal' | 'annotated-image';
	/** kind === 'markdown' */
	text?: string;
	/** kind === 'chart' */
	spec?: CsChartSpec;
	/** kind === 'diagram' — a process/flow diagram (nodes + directed edges). */
	diagram?: CsProcessSpec;
	/** kind === 'tool' — an inline tool-call chip at the position the call happened.
	 *  summary is a short result one-liner on the collapsed chip; input/result expand below. */
	tool?: { label: string; seconds: number; summary?: string; input?: string; result?: string };
	/** kind === 'stats' — response footer (pre-formatted for display). */
	stats?: { tokens: number; tokPerSec: number; ttfSeconds: number; totalSeconds: number; toolsSeconds: number; models: string[] };
	/** kind === 'artifact' — a downloadable file produced by the assistant (size pre-formatted). */
	artifact?: { id: number; name: string; size: string };
	/** kind === 'todos' — the assistant's task checklist for a multi-step report. */
	todos?: { items: { text: string; done: boolean }[]; doneCount: number };
	/** kind === 'terminal' — live/persisted console output of a code run.
	 *  status drives the traffic-light indicator: running (busy) → success → error. */
	terminal?: { text: string; status: 'running' | 'success' | 'error'; code?: string };
	/** kind === 'annotated-image' — a documentation screenshot with read-only annotation overlays. */
	image?: CsAnnotatedImageSpec;
}

const CHART_TYPES: string[] = [
	CsChartType.line, CsChartType.area, CsChartType.bar,
	CsChartType.column, CsChartType.pie, CsChartType.donut,
	CsChartType.waterfall, CsChartType.treemap, CsChartType.funnel,
	CsChartType.scatter, CsChartType.bubble, CsChartType.gauge,
	CsChartType.card, CsChartType.decomposition
];

const OPEN_FENCE = /```(chart|diagram|tool|stats|artifact|todos|terminal|annotated-image)(?:[ \t]+([^\r\n]+?))?[ \t]*\r?\n/g;

/**
 * Splits an assistant message into ordered markdown / chart segments by extracting ```chart
 * fenced blocks. While streaming, a not-yet-closed trailing block becomes a `chart-pending`
 * placeholder (we never parse half-received JSON). A closed block whose body is not a valid
 * spec falls back to a markdown segment holding the raw block, so a bad chart never breaks the
 * message.
 */
export function splitChatSegments(content: string, streaming: boolean): CsChatSegment[] {
	const text = content ?? '';
	const segments: CsChatSegment[] = [];
	OPEN_FENCE.lastIndex = 0;

	let cursor = 0;
	let m: RegExpExecArray | null;
	while ((m = OPEN_FENCE.exec(text)) !== null) {
		const fenceKind  = m[1];   // 'chart' | 'tool'
		const blockStart = m.index;
		const bodyStart  = m.index + m[0].length;
		const rest       = text.slice(bodyStart);
		const close      = /\r?\n```/.exec(rest);

		if (blockStart > cursor) {
			segments.push({ kind: 'markdown', text: text.slice(cursor, blockStart) });
		}

		if (!close) {
			// Unclosed trailing block. Only a CHART shows the "building chart…" placeholder —
			// its JSON body can take a moment to stream. Tool/stats/artifact fences are injected
			// whole (atomically) by the client, so a momentarily-unclosed one is just mid-flush
			// noise: render nothing for it rather than a bogus chart loader. Once the turn ends
			// (not streaming) any stray unclosed fence falls back to raw text.
			if (!streaming) {
				segments.push({ kind: 'markdown', text: text.slice(blockStart) });
			} else if (fenceKind === 'chart') {
				segments.push({ kind: 'chart-pending' });
			} else if (fenceKind === 'diagram') {
				segments.push({ kind: 'diagram-pending' });
			} else if (fenceKind === 'terminal') {
				// Live code run: render the console output streamed so far, marked running (busy).
				segments.push({ kind: 'terminal', terminal: { text: rest, status: 'running' } });
			}
			return segments;
		}

		const body     = rest.slice(0, close.index);
		const closeEnd = bodyStart + close.index + close[0].length;

		if (fenceKind === 'tool') {
			const tool = parseToolChip(body);
			segments.push(tool ? { kind: 'tool', tool }
			                   : { kind: 'markdown', text: text.slice(blockStart, closeEnd) });
		} else if (fenceKind === 'stats') {
			const stats = parseStats(body);
			if (stats) { segments.push({ kind: 'stats', stats }); }
			// An unparseable stats block is dropped silently — it's metadata, not content.
		} else if (fenceKind === 'artifact') {
			const artifact = parseArtifactChip(body);
			segments.push(artifact ? { kind: 'artifact', artifact }
			                       : { kind: 'markdown', text: text.slice(blockStart, closeEnd) });
		} else if (fenceKind === 'todos') {
			const todos = parseTodos(body);
			if (todos) { segments.push({ kind: 'todos', todos }); }
			// An unparseable todos block is dropped silently — it's a UI affordance, not content.
		} else if (fenceKind === 'terminal') {
			// Closed fence: info string is "<status> [ws=<id>]" — status is the FIRST token ("ok"/"error"),
			// any trailing tags (e.g. the ws= forensics tag) are ignored. Default to success.
			const status = (m[2] ?? '').split(/[ \t]+/)[0] === 'error' ? 'error' : 'success';
			segments.push({ kind: 'terminal', terminal: { text: body, status } });
		} else if (fenceKind === 'diagram') {
			const diagram = parseProcessSpec(body);
			segments.push(diagram ? { kind: 'diagram', diagram }
			                      : { kind: 'markdown', text: text.slice(blockStart, closeEnd) });
		} else if (fenceKind === 'annotated-image') {
			const image = parseAnnotatedImageSpec(body);
			// A bad/blocked image spec is dropped silently — it's an illustration, not content.
			if (image) { segments.push({ kind: 'annotated-image', image }); }
		} else {
			const spec = parseChartSpec(body);
			segments.push(spec ? { kind: 'chart', spec }
			                   : { kind: 'markdown', text: text.slice(blockStart, closeEnd) });
		}

		cursor = closeEnd;
		OPEN_FENCE.lastIndex = cursor;
	}

	let tail = text.slice(cursor);

	// While streaming, the last fence opener can arrive a character at a time — "```", "```st",
	// "```stats" — BEFORE its terminating newline, so OPEN_FENCE hasn't matched it yet. Without
	// this guard those bare backticks flash as raw text (the "```stats" flicker). Hide that
	// trailing partial opener; if it's the start of a chart, show the building placeholder instead.
	if (streaming && tail.length) {
		// 1–3 trailing backticks: the fence marker reveals a backtick at a time ("`", "``",
		// "```", "```st"), so match a partial run — not just a complete triple.
		const partial = /(?:\r?\n)*`{1,3}([a-z]*)[ \t]*$/i.exec(tail);
		if (partial) {
			const visible = tail.slice(0, partial.index);
			if (visible.trim().length) { segments.push({ kind: 'markdown', text: visible }); }
			const kw = partial[1].toLowerCase();
			if      (kw.length > 0 && 'chart'.startsWith(kw))   { segments.push({ kind: 'chart-pending' }); }
			else if (kw.length > 0 && 'diagram'.startsWith(kw)) { segments.push({ kind: 'diagram-pending' }); }
			return segments.length ? segments : [{ kind: 'markdown', text: '' }];
		}
	}

	if (tail.length) {
		segments.push({ kind: 'markdown', text: tail });
	}
	return segments.length ? segments : [{ kind: 'markdown', text }];
}

/** Recursive node validation, bounded: depth ≤ 6, ≤ 12 children per node (excess is dropped). */
function parseDecompositionNode(raw: any, depth: number): { label: string; value: number; children?: any[] } | null {
	if (!raw || typeof raw !== 'object') { return null; }
	const value = Number(raw.value);
	if (!Number.isFinite(value)) { return null; }
	const node: { label: string; value: number; children?: any[] } = {
		label: String(raw.label ?? ''),
		value
	};
	if (depth < 6 && Array.isArray(raw.children)) {
		const children = raw.children
			.slice(0, 12)
			.map((c: any) => parseDecompositionNode(c, depth + 1))
			.filter((c: any) => c !== null);
		if (children.length) { node.children = children; }
	}
	return node;
}

/**
 * Parses a ```annotated-image block into a validated spec. `src` must be same-origin/relative or a
 * data: URI (external URLs are rejected — they'd be CSP-blocked and are a potential exfil vector).
 * Highlight coordinates are clamped to 0–100 and enum-ish fields whitelisted. Bounded (≤ 60 boxes).
 * Returns null on bad JSON / disallowed src → caller drops the block.
 */
function parseAnnotatedImageSpec(body: string): CsAnnotatedImageSpec | null {
	let raw: any;
	try { raw = JSON.parse(body.trim()); } catch { return null; }
	if (!raw || typeof raw !== 'object' || typeof raw.src !== 'string') { return null; }

	const src = raw.src.trim();
	// Same-origin root-relative ("/…") or data: only — never http(s)/protocol-relative/other.
	if (!(src.startsWith('/') && !src.startsWith('//')) && !src.startsWith('data:')) { return null; }

	const clamp = (v: any) => Math.max(0, Math.min(100, Number(v) || 0));
	const POS   = ['top', 'bottom', 'left', 'right', 'inside'];
	const TYPES = ['info', 'success', 'warning', 'danger'];
	const str   = (v: any) => (typeof v === 'string' ? v : undefined);

	const highlights: CsImageHighlight[] = Array.isArray(raw.highlights)
		? raw.highlights.slice(0, 60).map((h: any): CsImageHighlight => ({
			x: clamp(h?.x), y: clamp(h?.y), width: clamp(h?.width), height: clamp(h?.height),
			color:          str(h?.color),
			thickness:      Number.isFinite(Number(h?.thickness)) ? Number(h.thickness) : undefined,
			dashed:         h?.dashed === true,
			radius:         Number.isFinite(Number(h?.radius)) ? Number(h.radius) : undefined,
			label:          str(h?.label),
			labelPosition:  POS.includes(h?.labelPosition) ? h.labelPosition : undefined,
			tooltip:        str(h?.tooltip),
			labelBgOpacity: Number.isFinite(Number(h?.labelBgOpacity)) ? Number(h.labelBgOpacity) : undefined,
			solidOpacity:   Number.isFinite(Number(h?.solidOpacity)) ? Number(h.solidOpacity) : undefined,
			infoType:       TYPES.includes(h?.infoType) ? h.infoType : undefined,
		}))
		: [];

	return { src, alt: str(raw.alt), highlights };
}

function parseToolChip(body: string): CsChatSegment['tool'] | null {
	try {
		const raw = JSON.parse(body.trim());
		if (!raw || typeof raw.tool !== 'string' || !raw.tool) { return null; }
		const ms = Number(raw.durationMs);
		// input arrives as a JSON object; pretty-print it for the expanded view. result is a string.
		const input = raw.input === undefined || raw.input === null
			? undefined
			: (typeof raw.input === 'string' ? raw.input : JSON.stringify(raw.input, null, 2));
		const result = typeof raw.result === 'string' ? raw.result : undefined;
		const summary = typeof raw.summary === 'string' ? raw.summary : undefined;
		return {
			label:   raw.tool,
			seconds: Number.isFinite(ms) ? Math.round(ms / 100) / 10 : 0,
			summary,
			input,
			result
		};
	} catch { return null; }
}

function parseTodos(body: string): CsChatSegment['todos'] | null {
	try {
		const raw   = JSON.parse(body.trim());
		const list  = Array.isArray(raw?.items) ? raw.items : (Array.isArray(raw) ? raw : null);
		if (!list) { return null; }
		const items = list
			.map((it: any) => ({ text: String(it?.text ?? '').trim(), done: it?.done === true }))
			.filter((it: { text: string }) => it.text.length > 0);
		if (!items.length) { return null; }
		return { items, doneCount: items.filter((it: { done: boolean }) => it.done).length };
	} catch { return null; }
}

function parseArtifactChip(body: string): { id: number; name: string; size: string } | null {
	try {
		const raw = JSON.parse(body.trim());
		const id  = Number(raw?.id);
		if (!Number.isInteger(id) || id <= 0 || typeof raw.name !== 'string' || !raw.name) { return null; }
		return { id, name: raw.name, size: formatBytes(Number(raw.sizeBytes)) };
	} catch { return null; }
}

function formatBytes(bytes: number): string {
	if (!Number.isFinite(bytes) || bytes <= 0) { return ''; }
	if (bytes < 1024)            { return `${bytes} B`; }
	if (bytes < 1024 * 1024)     { return `${Math.round(bytes / 1024 * 10) / 10} KB`; }
	return `${Math.round(bytes / (1024 * 1024) * 10) / 10} MB`;
}

function parseStats(body: string): CsChatSegment['stats'] | null {
	try {
		const raw      = JSON.parse(body.trim());
		const tokens   = Number(raw.outputTokens);
		const duration = Number(raw.durationMs);
		const ttf      = Number(raw.timeToFirstMs);
		const toolsMs = Number(raw.toolsMs);
		if (!Number.isFinite(duration) || duration <= 0) { return null; }
		// Honest generation rate: tool/DB time is the data source's cost, not the model's —
		// tok/s is computed over the time actually spent generating.
		const genMs = Math.max(1, duration - (Number.isFinite(toolsMs) ? toolsMs : 0));
		// Models that handled the turn — strip any "provider/" prefix for a compact footer label.
		const models = typeof raw.models === 'string'
			? raw.models.split(',').map((m: string) => m.trim().split('/').pop()!.trim()).filter(Boolean)
			: [];
		return {
			tokens:       Number.isFinite(tokens) ? tokens : 0,
			tokPerSec:    Number.isFinite(tokens) && tokens > 0 ? Math.round(tokens / (genMs / 1000) * 10) / 10 : 0,
			ttfSeconds:   Number.isFinite(ttf) ? Math.round(ttf / 100) / 10 : 0,
			totalSeconds: Math.round(duration / 100) / 10,
			toolsSeconds: Number.isFinite(toolsMs) ? Math.round(toolsMs / 100) / 10 : 0,
			models
		};
	} catch { return null; }
}

/**
 * Parses a ```diagram fenced block into a validated process graph. Unknown node kinds default to
 * `process`; duplicate node ids are dropped; edges referencing an unknown node, or a self-loop, are
 * dropped (a dangling edge never breaks the layout). Bounded (≤ 24 nodes, ≤ 60 edges) so a runaway
 * spec can't blow up rendering. Returns null on bad JSON / no usable node → caller shows raw text.
 */
function parseProcessSpec(body: string): CsProcessSpec | null {
	let raw: any;
	try { raw = JSON.parse(body.trim()); } catch { return null; }
	if (!raw || typeof raw !== 'object' || !Array.isArray(raw.nodes)) { return null; }

	const KINDS = ['start', 'process', 'decision', 'end'];
	const str   = (v: any) => (v != null && String(v).trim() ? String(v).trim() : undefined);

	const nodes: CsProcessNode[] = [];
	const seen = new Set<string>();
	for (const n of raw.nodes.slice(0, 24)) {
		if (!n || typeof n !== 'object') { continue; }
		const id = String(n.id ?? '').trim();
		if (!id || seen.has(id)) { continue; }
		seen.add(id);
		nodes.push({
			id,
			label:   str(n.label) ?? id,
			kind:    (KINDS.includes(String(n.kind)) ? String(n.kind) : 'process') as CsProcessNode['kind'],
			metric:  str(n.metric),
			tooltip: str(n.tooltip)
		});
	}
	if (!nodes.length) { return null; }

	const ids   = new Set(nodes.map(n => n.id));
	const edges: CsProcessEdge[] = [];
	for (const e of (Array.isArray(raw.edges) ? raw.edges : []).slice(0, 60)) {
		if (!e || typeof e !== 'object') { continue; }
		const from = String(e.from ?? '').trim();
		const to   = String(e.to ?? '').trim();
		if (!ids.has(from) || !ids.has(to) || from === to) { continue; }
		edges.push({ from, to, label: str(e.label) });
	}

	return {
		title:     str(raw.title),
		direction: raw.direction === 'TB' ? 'TB' : 'LR',
		nodes,
		edges
	};
}

function parseChartSpec(body: string): CsChartSpec | null {
	let raw: any;
	try { raw = JSON.parse(body.trim()); } catch { return null; }
	if (!raw || typeof raw !== 'object') { return null; }

	const type = String(raw.type ?? '').toLowerCase();
	if (!CHART_TYPES.includes(type)) { return null; }

	// Decomposition tree: a recursive `root` node instead of labels/datasets.
	if (type === CsChartType.decomposition) {
		const tree = parseDecompositionNode(raw.root, 0);
		return tree ? {
			type:  type as CsChartType,
			title: raw.title != null ? String(raw.title) : undefined,
			data:  { labels: [], datasets: [{ data: [tree.value] }], tree }
		} : null;
	}

	const datasets = (Array.isArray(raw.datasets) ? raw.datasets : [])
		.map((d: any) => ({
			label: d?.label != null ? String(d.label) : undefined,
			data:  (Array.isArray(d?.data) ? d.data : [])
				.map((n: any) => Number(n))
				.filter((n: number) => Number.isFinite(n))
		}))
		.filter((d: { data: number[] }) => d.data.length > 0);
	if (!datasets.length) { return null; }

	const labels      = (Array.isArray(raw.labels) ? raw.labels : []).map((l: any) => String(l));
	const finalLabels = labels.length ? labels : datasets[0].data.map((_: number, i: number) => String(i + 1));

	// Waterfall only: indices of absolute total/subtotal bars (values there are levels, not deltas).
	const totals = (Array.isArray(raw.totals) ? raw.totals : [])
		.map((n: any) => Number(n))
		.filter((n: number) => Number.isInteger(n) && n >= 0);

	// Gauge only: scale bounds and target marker.
	const num = (v: any) => (v != null && Number.isFinite(Number(v)) ? Number(v) : undefined);
	const min = num(raw.min), max = num(raw.max), target = num(raw.target);

	return {
		type:  type as CsChartType,
		title: raw.title != null ? String(raw.title) : undefined,
		data:  {
			labels: finalLabels, datasets,
			...(totals.length ? { totals } : {}),
			...(min !== undefined ? { min } : {}),
			...(max !== undefined ? { max } : {}),
			...(target !== undefined ? { target } : {})
		}
	};
}

/**
 * The `code=<base64>` tag on a ```terminal info line — the Python source that produced the block.
 * Base64 so the source rides on the single-line info string without colliding with its space
 * separator; UTF-8 safe via TextDecoder (atob alone mangles non-ASCII). Absent on older transcripts
 * written before the tag existed, hence optional. A malformed tag yields undefined rather than
 * throwing — a broken attribute must not take the terminal block down with it.
 */
function parseFenceCode(info: string): string | undefined {
	const m = /(?:^|[ 	])code=([A-Za-z0-9+/=]+)/.exec(info ?? '');
	if (!m) { return undefined; }
	try {
		const bin = atob(m[1]);
		const bytes = Uint8Array.from(bin, ch => ch.charCodeAt(0));
		return new TextDecoder().decode(bytes) || undefined;
	} catch { return undefined; }
}

/**
 * Encodes Python source for the ```terminal fence's `code=` tag — the inverse of
 * {@link parseFenceCode}. UTF-8 safe: btoa() alone throws on any non-Latin1 character (a comment
 * with an em dash is enough), so the string is encoded to bytes first.
 */
export function encodeCodeTag(code: string): string {
	const bytes = new TextEncoder().encode(code);
	let bin = '';
	bytes.forEach(b => { bin += String.fromCharCode(b); });
	return btoa(bin);
}

/**
 * Splits a persisted assistant turn into its body and the trailing ```error fence a failed turn
 * carries (written by ChatEndpoints). Returns the body unchanged and error=null for a normal turn.
 * The fence is ALWAYS last: it is appended after the response text when the stream throws.
 */
export function splitTrailingError(response: string): { body: string; error: string | null } {
	const text = response ?? '';
	const m = /```error\r?\n([\s\S]*?)\r?\n```[ \t]*$/.exec(text.trimEnd());
	if (!m) { return { body: text, error: null }; }
	return { body: text.trimEnd().slice(0, m.index).trimEnd(), error: m[1].trim() || 'Unknown error' };
}
